import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import {
  Badge,
  Button,
  Card,
  Chip,
  EmptyState,
  Input,
  Pagination,
  Select,
  Table,
} from '@/admin-design/_common';
import type { BadgeVariant } from '@/admin-design/_common';

/*
 * 이 화면이 한눈에 답해야 하는 것은 셋이다: 무슨 상품인가 · 돈이 되나 · 지금 팔리나.
 * 그래서 썸네일(텍스트만으로는 건기식 박스가 다 똑같아 보인다), 마진, 상태 묶음이
 * 행의 중심이고 나머지는 접었다.
 *
 * 열을 10개에서 6개로 줄였다: 프로바이더는 전부 wsmon 이라 정보가 없고, ID 는
 * 운영자가 실제로 쓰는 식별자가 아니며(상품코드를 쓴다), 싱크/판매/전시/도매품절
 * 네 열은 "지금 팔리나" 하나의 사실을 네 번 쪼갠 것이었다.
 */

const STATUS_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'pending', label: '대기' },
  { value: 'synced', label: '싱크완료' },
  { value: 'error', label: '에러' },
  { value: 'unlisted', label: '미등록' },
];
const STOCK_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'in_stock', label: '재고있음' },
  { value: 'sold_out', label: '품절' },
];
const SALE_STATUS_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'SALE', label: '판매중' },
  { value: 'SUSPENSION', label: '판매중지' },
  { value: 'OUTOFSTOCK', label: '품절' },
];
const DISPLAY_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'ON', label: '전시중' },
  { value: 'SUSPENSION', label: '전시중지' },
];
const EXCLUDED_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'hide', label: '취급' },
  { value: 'only', label: '제외' },
];

const PAGE_SIZE_OPTIONS = [50, 100, 500, 1000] as const;
const DEFAULT_PAGE_SIZE = 50;
const isValidPageSize = (n: number): n is (typeof PAGE_SIZE_OPTIONS)[number] =>
  (PAGE_SIZE_OPTIONS as readonly number[]).includes(n);

type CategoryStat = { category: string; total: number; live: number; excluded: number };
type SortKey = 'id' | 'name' | 'price' | 'expose_price' | 'updated_at';
type SortOrder = 'asc' | 'desc';

// 기본 화면은 '지금 팔리는 상품'이다. 전체를 보려면 필터에서 '전체'를 고른다.
const DEFAULTS = {
  status: 'all',
  stock: 'all',
  sale_status: 'SALE',
  display: 'ON',
  category: 'all',
  excluded: 'all',
  sort: 'updated_at' as SortKey,
  order: 'desc' as SortOrder,
  page: '1',
  per_page: String(DEFAULT_PAGE_SIZE),
  q: '',
};

export default function Products() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const status = params.get('status') ?? DEFAULTS.status;
  const stock = params.get('stock') ?? DEFAULTS.stock;
  const saleStatus = params.get('sale_status') ?? DEFAULTS.sale_status;
  const category = params.get('category') ?? DEFAULTS.category;
  const excluded = params.get('excluded') ?? DEFAULTS.excluded;
  const display = params.get('display') ?? DEFAULTS.display;
  const sort = (params.get('sort') ?? DEFAULTS.sort) as SortKey;
  const order = (params.get('order') ?? DEFAULTS.order) as SortOrder;
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const qParam = params.get('q') ?? '';
  const perPageRaw = Number(params.get('per_page') ?? DEFAULT_PAGE_SIZE);
  const pageSize = isValidPageSize(perPageRaw) ? perPageRaw : DEFAULT_PAGE_SIZE;

  const [searchInput, setSearchInput] = useState(qParam);
  useEffect(() => setSearchInput(qParam), [qParam]);
  useEffect(() => {
    const h = setTimeout(() => {
      if (searchInput !== qParam) updateParams({ q: searchInput, page: '1' });
    }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  // 상태 필터가 기본값과 다르면 펼쳐 둔다 — 접힌 채로 걸려 있으면
  // "상품이 왜 안 보이지" 가 된다.
  const [moreFiltersOpen, setMoreFiltersOpen] = useState<boolean | null>(null);

  const categories = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => api.productCategories() as Promise<CategoryStat[]>,
  });

  // 마진 계산에 배송비 역전과 수수료가 필요하다. 판매가−도매가만 보면
  // 흑자처럼 보이는 거래가 실제로는 적자인 경우가 있다.
  const schedule = useQuery({ queryKey: ['schedule'], queryFn: () => api.schedule() });
  const margin = useMemo(() => {
    const s = (schedule.data ?? {}) as Record<string, number>;
    return {
      supplierShip: s.margin_floor_supplier_ship ?? 3500,
      collectedShip: s.margin_floor_collected_ship ?? 3000,
      feeRate: s.margin_floor_fee_rate ?? 0.058,
      target: s.margin_floor_target_rate ?? 0.2,
    };
  }, [schedule.data]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['products', status, stock, saleStatus, display, category, excluded, page, pageSize, sort, order, qParam],
    queryFn: () =>
      api.products({
        status, sold_out: stock, sale_status: saleStatus, display, category, excluded,
        sort, order, q: qParam,
        limit: pageSize, offset: (page - 1) * pageSize,
      }),
    placeholderData: keepPreviousData,
  });

  const items: any[] = Array.isArray(data?.items) ? data.items : [];
  const total: number = typeof data?.total === 'number' ? data.total : 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const visibleIds = useMemo(() => items.map((i) => i.id as number), [items]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selected.has(id));

  const updateParams = (updates: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(updates)) {
      const def = (DEFAULTS as any)[k];
      if (v === '' || v === def) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: false });
  };

  const detailFilters = { status, stock, sale_status: saleStatus, display };
  const activeDetail = Object.entries(detailFilters).filter(
    ([k, v]) => v !== (DEFAULTS as any)[k],
  ).length;
  const showMoreFilters = moreFiltersOpen ?? activeDetail > 0;
  const anyFilter =
    activeDetail > 0 || category !== 'all' || excluded !== 'all' || qParam !== '';

  const resetFilters = () => {
    setSearchInput('');
    setParams(new URLSearchParams(), { replace: false });
  };

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });

  const [lastClickedId, setLastClickedId] = useState<number | null>(null);
  const toggleOne = (id: number, shiftKey = false) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const willCheck = !next.has(id);
      if (shiftKey && lastClickedId !== null && lastClickedId !== id) {
        const fromIdx = visibleIds.indexOf(lastClickedId);
        const toIdx = visibleIds.indexOf(id);
        if (fromIdx !== -1 && toIdx !== -1) {
          const [a, b] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
          for (let i = a; i <= b; i++) {
            if (willCheck) next.add(visibleIds[i]);
            else next.delete(visibleIds[i]);
          }
          return next;
        }
      }
      if (willCheck) next.add(id);
      else next.delete(id);
      return next;
    });
    setLastClickedId(id);
  };

  const changeSort = (k: SortKey) =>
    k === sort
      ? updateParams({ order: order === 'asc' ? 'desc' : 'asc', page: '1' })
      : updateParams({ sort: k, order: k === 'name' ? 'asc' : 'desc', page: '1' });

  type Action =
    | 'halt' | 'activate'                        // 판매+전시 동시
    | 'suspend' | 'resume'                       // 판매만
    | 'display_off' | 'display_on'               // 전시만
    | 'sold_out' | 'restock';                    // 재고

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['products'] });
    qc.invalidateQueries({ queryKey: ['product-categories'] });
    qc.invalidateQueries({ queryKey: ['stats'] });
    setSelected(new Set());
  };
  const action = useMutation({
    mutationFn: ({ ids, act }: { ids: number[]; act: Action }) => api.productsAction(ids, act),
    onSuccess: invalidate,
  });
  const dbDelete = useMutation({
    mutationFn: (ids: number[]) => api.deleteProducts(ids),
    onSuccess: invalidate,
  });
  const registerSelected = useMutation({
    mutationFn: (ids: number[]) => api.createJob('register', { product_ids: ids, display_on: false }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      setSelected(new Set());
    },
  });

  const onAction = (act: Action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const labels: Record<string, string> = {
      halt: '판매중지 + 전시중지', activate: '판매재개 + 전시노출',
      suspend: '판매중지', resume: '판매재개',
      display_off: '전시중지', display_on: '전시노출',
      sold_out: '품절', restock: '재입고',
    };
    if (!confirm(`선택한 ${ids.length}개 상품을 "${labels[act]}" 처리합니다.`)) return;
    action.mutate({ ids, act });
  };
  const onDbDelete = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`선택한 ${ids.length}개를 DB에서만 영구 삭제합니다.\n네이버 등록 상태는 바뀌지 않습니다.`)) return;
    dbDelete.mutate(ids);
  };
  const onRegister = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`선택한 ${ids.length}개를 네이버에 등록합니다. 전시중지 상태로 올라갑니다.`)) return;
    registerSelected.mutate(ids);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-3">
        <h1 className="text-2xl font-bold">상품</h1>
        <span className="text-sm text-muted-foreground tabular-nums">
          {total.toLocaleString()}건
          {anyFilter ? ' · 필터 적용됨' : ' · 판매중·전시중'}
        </span>
        {anyFilter && (
          <button onClick={resetFilters} className="text-sm text-muted-foreground hover:text-foreground underline">
            필터 초기화
          </button>
        )}
      </div>

      {/* 자주 쓰는 셋만 위에 두고, 상태 계열 넷은 접어 둔다. */}
      <Card className="p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div className="relative">
            <Input
              type="text"
              placeholder="상품명 또는 상품코드"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pr-8 w-72"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="검색어 지우기"
              >
                ✕
              </button>
            )}
          </div>

          <Select
            className="w-56"
            value={category}
            onValueChange={(v) => updateParams({ category: v, page: '1' })}
            options={[
              { value: 'all', label: '분류 전체' },
              ...(categories.data ?? []).map((c) => ({
                value: c.category,
                label: `${c.category} ${c.live}`,
              })),
            ]}
          />

          <FilterGroup
            label="취급"
            options={EXCLUDED_OPTIONS}
            value={excluded}
            onChange={(v) => updateParams({ excluded: v, page: '1' })}
          />

          <div className="flex-1" />
          <button
            onClick={() => setMoreFiltersOpen(!showMoreFilters)}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            상태 필터 {showMoreFilters ? '접기' : '펼치기'}
            {activeDetail > 0 && <Badge variant="info" className="ml-1.5">{activeDetail}</Badge>}
          </button>
        </div>

        {showMoreFilters && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm border-t border-border pt-3">
            <FilterGroup label="싱크" options={STATUS_OPTIONS} value={status}
              onChange={(v) => updateParams({ status: v, page: '1' })} />
            <FilterGroup label="도매재고" options={STOCK_OPTIONS} value={stock}
              onChange={(v) => updateParams({ stock: v, page: '1' })} />
            <FilterGroup label="판매" options={SALE_STATUS_OPTIONS} value={saleStatus}
              onChange={(v) => updateParams({ sale_status: v, page: '1' })} />
            <FilterGroup label="전시" options={DISPLAY_OPTIONS} value={display}
              onChange={(v) => updateParams({ display: v, page: '1' })} />
          </div>
        )}
      </Card>

      {selected.size > 0 && (
        <Card className="p-3 border-primary/40 bg-primary/5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="text-sm">
              <span className="font-semibold tabular-nums">{selected.size}개</span> 선택됨
              <button onClick={() => setSelected(new Set())}
                className="ml-2 text-xs text-muted-foreground hover:underline">해제</button>
            </div>
            {/* 상품을 통째로 내리거나 올리는 게 실제로 가장 잦은 동작이라 앞에 둔다. */}
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="destructive" onClick={() => onAction('halt')} loading={action.isPending}>
                내리기
              </Button>
              <Button size="sm" variant="primary" onClick={() => onAction('activate')} loading={action.isPending}>
                올리기
              </Button>
              <span className="text-xs text-muted-foreground">판매·전시 동시</span>
            </div>

            <span className="h-5 border-l border-border" />

            <ActionGroup label="판매">
              <Button size="sm" variant="secondary" onClick={() => onAction('suspend')} loading={action.isPending}>중지</Button>
              <Button size="sm" variant="secondary" onClick={() => onAction('resume')} loading={action.isPending}>재개</Button>
            </ActionGroup>
            <ActionGroup label="전시">
              <Button size="sm" variant="secondary" onClick={() => onAction('display_off')} loading={action.isPending}>중지</Button>
              <Button size="sm" variant="secondary" onClick={() => onAction('display_on')} loading={action.isPending}>노출</Button>
            </ActionGroup>
            <ActionGroup label="재고">
              <Button size="sm" variant="secondary" onClick={() => onAction('sold_out')} loading={action.isPending}>품절</Button>
              <Button size="sm" variant="secondary" onClick={() => onAction('restock')} loading={action.isPending}>재입고</Button>
            </ActionGroup>
            <div className="flex-1" />
            <div className="flex gap-2">
              <Button size="sm" variant="primary" onClick={onRegister} loading={registerSelected.isPending}>
                네이버 등록
              </Button>
              <Button as={Link} to={`/bulk-adjust?product_ids=${Array.from(selected).join(',')}`} size="sm" variant="secondary">
                가격 조정
              </Button>
              <Button size="sm" variant="secondary" onClick={onDbDelete} loading={dbDelete.isPending}
                className="text-destructive border-destructive-soft hover:bg-destructive-soft">
                DB 삭제
              </Button>
            </div>
          </div>
        </Card>
      )}

      {registerSelected.isSuccess && <Notice tone="ok">등록 작업을 큐에 넣었습니다. Jobs 에서 진행 상황을 볼 수 있습니다.</Notice>}
      {action.isSuccess && (() => {
        const d = action.data as any;
        const bad = (d?.naver_fail ?? 0) > 0 || (d?.db_failed ?? 0) > 0;
        return (
          <Notice tone={bad ? 'bad' : 'ok'}>
            네이버 {d?.naver_ok ?? 0}개 반영
            {(d?.naver_fail ?? 0) > 0 && ` · ${d.naver_fail}개 실패`}
            {' · '}목록 {d?.db_updated ?? 0}개 갱신
            {(d?.db_failed ?? 0) > 0 && ` · ${d.db_failed}개 갱신 실패`}
            {Array.isArray(d?.db_errors) && d.db_errors.length > 0 && (
              <span className="block text-xs mt-0.5">{d.db_errors.join(' / ')}</span>
            )}
            {Array.isArray(d?.naver_errors) && d.naver_errors.length > 0 && (
              <span className="block text-xs mt-0.5">{d.naver_errors.join(' / ')}</span>
            )}
          </Notice>
        );
      })()}
      {action.isError && <Notice tone="bad">처리하지 못했습니다: {(action.error as Error).message}</Notice>}
      {dbDelete.isSuccess && <Notice tone="ok">DB 에서 {(dbDelete.data as any)?.deleted ?? 0}개를 삭제했습니다.</Notice>}

      <Card className="overflow-hidden overflow-x-auto">
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header className="w-8">
                <input type="checkbox" checked={allSelected} aria-label="전체 선택"
                  ref={(el) => { if (el) el.indeterminate = someSelected; }} onChange={toggleAll} />
              </Table.Header>
              <Table.Header className="w-14" />
              <SortHeader k="name" sort={sort} order={order} onClick={changeSort}>상품</SortHeader>
              <SortHeader k="price" sort={sort} order={order} onClick={changeSort} align="right" className="w-32">
                도매가
              </SortHeader>
              <Table.Header className="text-right w-36">판매가 · 마진</Table.Header>
              <SortHeader k="expose_price" sort={sort} order={order} onClick={changeSort} align="right" className="w-28">
                노출최저가
              </SortHeader>
              <Table.Header className="w-28">상태</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {isLoading && (
              <Table.Row className="hover:bg-transparent">
                <Table.Cell colSpan={7} className="text-center py-10 text-muted-foreground">불러오는 중…</Table.Cell>
              </Table.Row>
            )}
            {!isLoading && items.length === 0 && (
              <Table.Row className="hover:bg-transparent">
                <Table.Cell colSpan={7} className="p-0">
                  <EmptyState
                    title={qParam ? `"${qParam}" 에 해당하는 상품이 없습니다` : '조건에 맞는 상품이 없습니다'}
                    description={anyFilter ? '필터를 줄이면 더 많은 상품이 보입니다.' : undefined}
                  />
                </Table.Cell>
              </Table.Row>
            )}
            {items.map((p: any) => {
              const sell: number | null = p.synced_price ?? p.naver_price ?? null;
              const net = sell != null && p.price > 0 ? realMargin(sell, p.price, margin) : null;
              // 도매처가 실제로 건 최저가만 위반 판정에 쓴다. expose_price 는 0일 때
              // 도매가×1.5 로 대체되므로 그걸 쓰면 최저가 없는 상품이 전부 미달로 뜬다.
              const floor: number = p.supplier_expose_price ?? 0;
              const belowFloor = sell != null && floor > 0 && sell < floor;
              return (
                <Table.Row key={p.id} className={selected.has(p.id) ? 'bg-primary/10' : ''}>
                  <Table.Cell>
                    <input
                      type="checkbox"
                      aria-label={`${p.name} 선택`}
                      checked={selected.has(p.id)}
                      onChange={() => { /* shiftKey 를 잡으려면 onClick 에서 처리해야 한다 */ }}
                      onClick={(e: MouseEvent<HTMLInputElement>) => toggleOne(p.id, e.shiftKey)}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Link to={`/products/${p.id}`} className="block">
                      <Thumb src={p.main_image_url} alt="" dimmed={p.excluded} />
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <Link to={`/products/${p.id}`} className="text-foreground hover:underline line-clamp-2">
                      {highlight(p.name, qParam)}
                    </Link>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-muted-foreground">
                      <span className="font-mono">{p.provider_product_id}</span>
                      {p.category && <span>{p.category}</span>}
                      {p.excluded && <Badge variant="muted" title={p.excluded_reason}>제외</Badge>}
                      {p.sold_out && <Badge variant="destructive">도매품절</Badge>}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-right tabular-nums text-muted-foreground">
                    {p.price.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular-nums">
                    {sell == null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <>
                        <div>{sell.toLocaleString()}</div>
                        {net && <MarginLine net={net} target={margin.target} />}
                      </>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular-nums">
                    {floor > 0 ? (
                      <>
                        <span className={belowFloor ? 'text-destructive font-medium' : 'text-muted-foreground'}>
                          {floor.toLocaleString()}
                        </span>
                        {belowFloor && <div className="text-[11px] text-destructive">미달</div>}
                      </>
                    ) : (
                      <span className="text-muted-foreground" title="도매처가 노출최저가를 걸지 않았습니다">—</span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col gap-1 items-start">
                      <SaleBadge status={p.sale_status} />
                      <DisplayBadge status={p.display_status} />
                      {p.listing_status !== 'synced' && <StatusBadge status={p.listing_status} />}
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground gap-3">
        <div className="flex items-center gap-3">
          {total > 0 && (
            <span className="tabular-nums">
              {((page - 1) * pageSize + 1).toLocaleString()}–
              {Math.min(page * pageSize, total).toLocaleString()} / {total.toLocaleString()}
            </span>
          )}
          <label className="flex items-center gap-1">
            <span>페이지당</span>
            <select
              className="border rounded px-2 py-1 bg-background"
              value={pageSize}
              onChange={(e) => updateParams({ per_page: e.target.value, page: '1' })}
            >
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages}
            onChange={(p) => updateParams({ page: String(p) })} disabled={isFetching} />
        )}
      </div>
    </div>
  );
}

/* ---------- 표시용 조각 ---------- */

// realMargin — 판매가에서 도매가만 빼면 흑자로 보이는 거래가 실제로는 적자일 수 있다.
// 도매처 배송비와 네이버에서 받는 배송비의 차액, 그리고 결제금액 기준 수수료를 함께 뺀다.
function realMargin(
  sell: number,
  cost: number,
  m: { supplierShip: number; collectedShip: number; feeRate: number },
) {
  const amount = sell - cost - (m.supplierShip - m.collectedShip) - (sell + m.collectedShip) * m.feeRate;
  return { amount: Math.round(amount), rate: amount / sell };
}

function MarginLine({ net, target }: { net: { amount: number; rate: number }; target: number }) {
  const tone =
    net.amount < 0 ? 'text-destructive'
      : net.rate < target ? 'text-warning'
        : 'text-muted-foreground';
  return (
    <div className={`text-xs ${tone}`}>
      {net.amount >= 0 ? '+' : '−'}{Math.abs(net.amount).toLocaleString()} ({Math.round(net.rate * 100)}%)
    </div>
  );
}

function Thumb({ src, alt, dimmed }: { src?: string; alt: string; dimmed?: boolean }) {
  if (!src) {
    return <div className="w-12 h-12 rounded bg-muted" aria-hidden="true" />;
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`w-12 h-12 rounded object-contain bg-white border border-border ${dimmed ? 'opacity-40' : ''}`}
    />
  );
}

function Notice({ tone, children }: { tone: 'ok' | 'bad'; children: ReactNode }) {
  return (
    <p className={`text-sm ${tone === 'ok' ? 'text-success' : 'text-destructive'}`}>{children}</p>
  );
}

function ActionGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function highlight(text: string, q: string) {
  if (!q) return text;
  const parts = text.split(new RegExp(`(${escapeRegex(q)})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-yellow-200 text-slate-900 dark:bg-yellow-500/40 dark:text-yellow-50 rounded px-0.5">{part}</mark>
      : <span key={i}>{part}</span>,
  );
}
function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function FilterGroup({
  label, options, value, onChange,
}: { label: string; options: { value: string; label: string }[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex gap-1">
        {options.map((o) => (
          <Chip key={o.value} shape="square" active={value === o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function SortHeader({
  k, sort, order, onClick, children, align, className,
}: {
  k: SortKey; sort: SortKey; order: SortOrder; onClick: (k: SortKey) => void;
  children: ReactNode; align?: 'right'; className?: string;
}) {
  const active = sort === k;
  return (
    <Table.Header className={`${align === 'right' ? 'text-right' : ''} ${className || ''}`}>
      <button onClick={() => onClick(k)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${active ? 'text-foreground' : 'text-muted-foreground'}`}>
        {children}
        <span className="text-xs">{active ? (order === 'asc' ? '▲' : '▼') : '⇅'}</span>
      </button>
    </Table.Header>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="muted">미등록</Badge>;
  const map: Record<string, BadgeVariant> = { synced: 'success', pending: 'warning', error: 'destructive' };
  const labels: Record<string, string> = { synced: '싱크완료', pending: '대기', error: '에러' };
  return <Badge variant={map[status] ?? 'muted'}>{labels[status] ?? status}</Badge>;
}

function DisplayBadge({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const labels: Record<string, string> = { ON: '전시중', SUSPENSION: '전시중지' };
  const map: Record<string, BadgeVariant> = { ON: 'success', SUSPENSION: 'muted' };
  return <Badge variant={map[status] ?? 'muted'}>{labels[status] || status}</Badge>;
}

function SaleBadge({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const labels: Record<string, string> = {
    SALE: '판매중', SUSPENSION: '판매중지', OUTOFSTOCK: '품절',
    CLOSE: '판매종료', PROHIBITION: '판매금지', WAIT: '대기',
  };
  const map: Record<string, BadgeVariant> = {
    SALE: 'success', SUSPENSION: 'destructive', OUTOFSTOCK: 'warning',
  };
  return <Badge variant={map[status] ?? 'muted'}>{labels[status] || status}</Badge>;
}
